/* eslint-disable no-useless-escape */

import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from "@capacitor/push-notifications";

import { Capacitor } from "@capacitor/core";
import { registerDevice } from "../services/notifications.service";
import { ENV } from "../environments/environment";
import { holdUpdateReloadForPermissionPrompt } from "../serviceWorkerRegistration";

export const urlB64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, "+")
    .replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const registerWebNotifications = async ({ studyId }: { studyId: number }) => {
  if (!ENV.VAPID_PUBLIC_KEY) {
    console.error("Missing VAPID public key — set REACT_APP_VAPID_PUBLIC_KEY at build time");
    return;
  }
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    // e.g. iOS Safari tabs (web push only works from the home screen) and in-app WebViews
    return;
  }
  let releaseUpdateReload: (() => void) | undefined;
  try {
    // Reuse the worker registered by serviceWorkerRegistration.register().
    const registration = await navigator.serviceWorker.ready;
    if ("Notification" in window && Notification.permission === "default") {
      // subscribe() shows the permission prompt; an update reload must not close it.
      releaseUpdateReload = holdUpdateReloadForPermissionPrompt();
    }
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: ENV.VAPID_PUBLIC_KEY
    });

    registerDevice(
      { studyId },
      { type: "WEB", deviceCredentials: subscription }
    );
  } catch (error) {
    console.warn("Web push subscription failed", error);
  } finally {
    if (releaseUpdateReload) {
      releaseUpdateReload();
    }
  }
};

const registerMobileNotitification = async ({ studyId }: { studyId: number }) => {
  const register = () => {

    // Register with Apple / Google to receive push via APNS/FCM
    PushNotifications.register();

    // On success, we should be able to receive notifications
    PushNotifications.addListener("registration", (token: Token) => {
      console.log("Push registration success");
      registerDevice(
        { studyId },
        { type: "MOBILE", deviceCredentials: { token: token.value } }
      );
    });

    // Some issue with our setup and push will not work
    PushNotifications.addListener("registrationError", (error: any) => {
      console.log("Error on registration: " + JSON.stringify(error));
    });

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener(
      "pushNotificationReceived",
      (notification: PushNotificationSchema) => { }
    );

    // Method called when tapping on a notification
    PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (notification: ActionPerformed) => { }
    );

    PushNotifications.createChannel({
      id: '1',
      name: 'Study Notifications',
      description: 'Study notifications',
      importance: 4,
      visibility: 1,
      lights: true,
      vibration: true,
    })
  };

  let currentPermission = await PushNotifications.checkPermissions()
  if (currentPermission.receive !== 'granted') {
    currentPermission = await PushNotifications.requestPermissions()
  }

  if (currentPermission.receive === 'granted') {
    register()
  }
};

const unregisterMobileNotifications = () => {

}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const unregisterWebNotification = async () => {
  try
  {
    const registration = await navigator.serviceWorker.getRegistration();
    await registration.unregister();
  }
  catch (error)
  {
    console.warn("Cannot get service worker to unregister")
  }

};


export const registerNotifications = async ({ studyId }: { studyId: number }) => {
  if (Capacitor.isNativePlatform()) {
    await registerMobileNotitification({ studyId });
    return unregisterMobileNotifications;
  } else {
    registerWebNotifications({ studyId });
    return unregisterWebNotification;
  }
}